import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ConfirmationService, MessageService, SelectItem } from 'primeng';
import { Message } from 'src/app/classes/Message';
import { LoggerService } from 'src/app/services/logger.service';
import { OrganisationSpecificStepsService } from 'src/app/services/organisation-specific-steps.service';
import { TaggedLogger } from 'src/app/services/taggedLogger';
import { IAnswer, IAnswerTemplate } from '../../../interfaces/answer';
import { NoEmptyString } from '../../../interfaces/no-empty-string.validator';
import {
  IOrganisationSpecificStepGroup,
  IOrganisationSpecificSteps,
} from '../../../interfaces/organisation-specific-steps';
import { OrganisationsService } from '../../../services';
import { Observable, Subscription } from 'rxjs';
import { Loader } from '../../../utils/loader';

enum QuestionTypes {
  TickBox,
  MultiSelect,
  SingleSelect,
  FreeText,
}

@Component({
  selector: 'app-add-organisation-specific-step',
  templateUrl: './add-organisation-specific-step.component.html',
  styleUrls: ['./add-organisation-specific-step.component.css'],
})
export class AddOrganisationSpecificStepComponent implements OnInit {
  private readonly logger: TaggedLogger;
  /**
   * An async loader
   * @private
   */
  private readonly loader: Loader;

  get isBusy() {
    if (!this.loader) return true; // If we've not set this up yet, we're busy

    return this.loader.isBusy;
  }

  public addStepForm: UntypedFormGroup = null;
  public freeTextTypes: any[];
  public questionTypes: any[];
  public newAnswerText: string;
  public answerColumns: any[];
  public multiChoiceQuestionId: number;
  public allowPhotographs: boolean;
  public isSaving: boolean = false;
  private _organisationSpecificStep: IOrganisationSpecificSteps;
  public ancillarySteps: SelectItem[];

  @Input()
  get organisationSpecificStep(): IOrganisationSpecificSteps {
    return this._organisationSpecificStep;
  }

  set organisationSpecificStep(value: IOrganisationSpecificSteps) {
    if (value) {
      this._organisationSpecificStep = value;

      this.buildFormValues(this._organisationSpecificStep);
    }
  }

  private selectedChecklistChangeSubscription: Subscription;
  @Input() selectedChecklistEvent: Observable<IOrganisationSpecificStepGroup>;

  private stepListChangeSubscription: Subscription;
  @Input() stepListChangedEvent: Observable<IOrganisationSpecificSteps[]>;

  public selectedChecklist: IOrganisationSpecificStepGroup;

  @Output() closeDialog: EventEmitter<any>;
  @Output() checklistStepUpdated: EventEmitter<any>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private organisationsService: OrganisationsService,
    private confirmationService: ConfirmationService,
    private organisationSpecificStepService: OrganisationSpecificStepsService,
    private messageService: MessageService,
    loggerService: LoggerService,
  ) {
    this.answerColumns = [
      { field: 'text', header: '' },
      { field: 'allowPhotos', header: 'Allow Photos' },
      { field: 'minimumNumberOfPhotos', header: 'Min # Photos' },
    ];
    this.closeDialog = new EventEmitter();
    this.checklistStepUpdated = new EventEmitter();
    this.logger = loggerService.taggedLogger('component:add-organisation-specific-step');
    this.loader = Loader.create('component:add-organisation-specific-step', loggerService);
  }

  ngOnInit() {
    this.loadAncillarySteps();
    this.loadQuestionTypes();

    this.selectedChecklistChangeSubscription = this.selectedChecklistEvent.subscribe(
      (checklist) => {
        this.logger.debug('Checklist Changed:', checklist && checklist.id);

        if (checklist) {
          this.selectedChecklist = checklist;
        }
      },
    );
  }

  loadAncillarySteps() {
    this.organisationSpecificStepService.getAncillarySteps().then((res) => {
      this.ancillarySteps = res.map((x) => {
        return { label: x.stepName, value: x.id };
      });
    });
  }

  buildFormValues(organisationStep: IOrganisationSpecificSteps) {
    this.addStepForm = this.formBuilder.group({
      id: new UntypedFormControl(organisationStep.id || 0, null),
      name: new UntypedFormControl(
        organisationStep.name,
        Validators.compose([Validators.required, NoEmptyString]),
      ),
      order: new UntypedFormControl(organisationStep.order || 1, null),
      isPickup: new UntypedFormControl(
        organisationStep.isPickUp || false,
        Validators.compose([Validators.required]),
      ),
      organisationId: new UntypedFormControl(organisationStep.organisationId, null),
      isActive: new UntypedFormControl(
        organisationStep.isActive || true,
        Validators.compose([Validators.required]),
      ),
      organisationSpecificStepsGroupId: new UntypedFormControl(
        organisationStep.organisationSpecificStepsGroupId,
        Validators.compose([Validators.required]),
      ),
      groupName: new UntypedFormControl(organisationStep.groupName, null),
      isDefaultGroup: new UntypedFormControl(organisationStep.isDefaultGroup || false, null),
      allowFreeText: new UntypedFormControl(organisationStep.allowFreeText || false, null),
      description: new UntypedFormControl(organisationStep.description, null),
      freeTextTypeId: new UntypedFormControl(organisationStep.freeTextTypeId || 1, null),
      hasPII: new UntypedFormControl(organisationStep.hasPII || false, null),
      imageId: new UntypedFormControl(organisationStep.imageId, null),
      isQuestion: new UntypedFormControl(organisationStep.isQuestion || false, null),
      isStep: new UntypedFormControl(organisationStep.isStep || false, null),
      minPhotosAllowed: new UntypedFormControl(organisationStep.minPhotosAllowed, null),
      parentVersionId: new UntypedFormControl(organisationStep.parentVersionId, null),
      placeholderText: new UntypedFormControl(organisationStep.placeholderText),
      questionTypeId: new UntypedFormControl(organisationStep.questionTypeId || 2, null),
      isFreeTextMandatory: new UntypedFormControl(
        organisationStep.isFreeTextMandatory || false,
        null,
      ),
      allowPhotos: new UntypedFormControl(organisationStep.minPhotosAllowed != null, null),
      isMultiChoiceSelection: new UntypedFormControl(
        organisationStep.questionTypeId == this.multiChoiceQuestionId,
        null,
      ),
      answerTemplates: this.formBuilder.array([]),
      ancillaryStepId: new UntypedFormControl(organisationStep.ancillaryStepId),
    });

    console.log('FTI ', organisationStep.freeTextTypeId);
  }

  loadQuestionTypes() {
    this.organisationSpecificStepService
      .getFreeTextTypes()
      .then((res) => {
        this.freeTextTypes = [{ label: 'Select Free Text Type', value: null }];
        if (res) {
          res.forEach((x) => {
            if (x.name != null && x.name.length > 0) {
              this.freeTextTypes.push({
                label: x.name,
                value: x.id,
              });
            }
          });
        }
      })
      .catch((err) => {
        this.logger.error(err);
      });

    this.organisationSpecificStepService
      .getQuestionTypes()
      .then((res) => {
        this.questionTypes = [];
        if (res) {
          res.forEach((x) => {
            this.questionTypes.push({
              label: x.name,
              value: x.id,
            });
          });
        }
        let multiChoiceQtn = this.questionTypes.find(
          (x) => x.label == QuestionTypes[QuestionTypes.MultiSelect],
        );
        if (multiChoiceQtn) this.multiChoiceQuestionId = multiChoiceQtn.value;
      })
      .catch((err) => {
        this.logger.error(err);
      });
  }

  hasDescription(organisationStep: IOrganisationSpecificSteps): boolean {
    var isSelect =
      organisationStep.isQuestion &&
      (organisationStep.questionTypeId == QuestionTypes.SingleSelect ||
        organisationStep.questionTypeId == QuestionTypes.MultiSelect);
    var allowPhotos = organisationStep.allowPhotos;
    var allowFreeText = organisationStep.allowFreeText;
    if (isSelect || allowPhotos || allowFreeText) {
      return !!organisationStep.description;
    }
    return true;
  }

  get f() {
    return this.addStepForm.value;
  }

  async saveStep(formValue: IOrganisationSpecificSteps) {
    const taskId = this.loader.addTask('saveStep()');
    try {
      if (!this.hasDescription(formValue)) {
        this.messageService.add(Message.Error('Please ensure description is valid'));
        return;
      }
      if (!this.addStepForm.valid) {
        this.logger.error('Form was not valid', this.addStepForm);
        this.messageService.add(Message.Error('Please check that the step is valid'));
        throw new Error('Form input was invalid');
      }

      // todo: this looks like custom validation?
      if (this.organisationSpecificStep && this.organisationSpecificStep.answerTemplates) {
        formValue.answerTemplates = this.organisationSpecificStep.answerTemplates;
      }

      // EN-3287 / EN-3982: a single-answer (non-multichoice) step can't have a
      // required answer - drivers are offered mutually exclusive options but forced
      // to pick the required one, so the other answers (and their photos) are lost.
      // Ask the user whether to switch to multi-selection, then save; cancelling
      // leaves the step untouched so they can untick the required answers instead.
      if (this.hasInvalidSingleAnswerRequiredCombo(formValue)) {
        this.confirmEnableMultiChoice();
        return;
      }

      if (formValue.isQuestion) {
        formValue.questionTypeId = QuestionTypes.SingleSelect;
        if (formValue.isMultiChoiceSelection && this.multiChoiceQuestionId) {
          formValue.questionTypeId = this.multiChoiceQuestionId;
        }
      } else {
        formValue.questionTypeId = undefined;
      }

      if (formValue.allowPhotos) {
        if (isNaN(formValue.minPhotosAllowed)) {
          formValue.minPhotosAllowed = 0;
        }
      } else {
        formValue.minPhotosAllowed = null;
      }

      if (formValue.id) {
        await this.organisationsService.updateChecklistItem(formValue);
      } else {
        await this.organisationsService.saveStep(formValue);
      }
      // Also close if everything was good?
      this.handleStepUpdated();
    } catch (error) {
      this.logger.error(error);
      this.messageService.add(Message.Error(JSON.stringify(error), 'Error'));
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * EN-3287: how many answer templates are flagged as required.
   */
  private requiredAnswerCount(): number {
    const answers = this.organisationSpecificStep?.answerTemplates;
    return answers ? answers.filter((a) => a.required).length : 0;
  }

  /**
   * EN-3287 / EN-3982: true when choice selection is single-answer yet an answer
   * is required. More than one loops on the mobile app; exactly one forces drivers
   * onto that answer and discards the others' photos.
   */
  hasInvalidSingleAnswerRequiredCombo(
    formValue: IOrganisationSpecificSteps = this.addStepForm?.value,
  ): boolean {
    return (
      !!formValue &&
      formValue.isQuestion &&
      !formValue.isMultiChoiceSelection &&
      this.requiredAnswerCount() > 0
    );
  }

  /**
   * EN-3287 / EN-3982: asks whether to switch the step to multi-selection so the
   * required answers become valid, then saves. Cancelling leaves the step
   * untouched so the user can untick the required answers instead.
   */
  private confirmEnableMultiChoice(): void {
    this.confirmationService.confirm({
      key: 'confirmAction',
      header: "This step can't be completed correctly by drivers",
      message:
        "A single-answer step can't have required answers, as drivers would be " +
        'forced to pick the required answer over the others. ' +
        'Enable multi-selection so answers can be required?',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Enable multi-selection',
      rejectLabel: 'Cancel',
      accept: () => {
        this.addStepForm.patchValue({ isMultiChoiceSelection: true });
        this.saveStep(this.addStepForm.value);
      },
      reject: () => {},
    });
  }

  addAnswerToStep() {
    if (this.newAnswerText) {
      if (
        !this.organisationSpecificStep.answerTemplates ||
        this.organisationSpecificStep.answerTemplates.length == 0
      )
        this.organisationSpecificStep.answerTemplates = [];
      this.organisationSpecificStep.answerTemplates.push(<IAnswerTemplate>{
        answer: <IAnswer>{
          text: this.newAnswerText,
          organisationId: this.organisationSpecificStep.organisationId,
          parentVersionId: 0, // ToDO: Fix this
        },
        displayOrder: this.organisationSpecificStep.answerTemplates.length + 1, // ToDo: when we put in reordering, sort this
      });
      var newProp = this.organisationSpecificStep.answerTemplates;
      this.organisationSpecificStep.answerTemplates = [...newProp];
      this.newAnswerText = null;
    }
  }

  handleStepUpdated = () => this.checklistStepUpdated.emit();
  handleCloseDialog = () => this.closeDialog.emit();

  handleAllowPhotosChange(value) {
    this.allowPhotographs = value;

    if (value == true) {
      if (this.addStepForm.value.minPhotosAllowed == null) {
        this.addStepForm.patchValue({ minPhotosAllowed: 0 });
      }
    } else {
      this.addStepForm.patchValue({ minPhotosAllowed: null });
    }
  }

  handleAnswerDelete = (orgStepId, answerId, index) => {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this item?',
      header: 'Confirmation',
      icon: 'pi pi-question',
      key: 'confirmAction',
      accept: () => {
        // reinstate the checklist
        //if this answer already exists, remove it from the answer templates
        if (answerId) {
          this.organisationSpecificStepService
            .deleteAnswerTemplate(orgStepId, answerId)
            .then((res) => {
              if (res) {
                this.updateStepAnswers(index);
                this.messageService.add(Message.Success('Answer Deleted', 'Success'));
              } else {
                this.messageService.add(
                  Message.Error('An error occurred deleting the Answer', 'Error'),
                );
              }
            })
            .catch((err) => {
              this.logger.error(err);
            });
        } else {
          this.updateStepAnswers(index);
        }
      },
      reject: () => {},
    });
  };
  updateStepAnswers = (index) => {
    this.organisationSpecificStep.answerTemplates.splice(index, 1);
    this.organisationSpecificStep.answerTemplates = [
      ...this.organisationSpecificStep.answerTemplates,
    ];
  };

  uniqueAnswerNames() {
    const answerTemplates = this.organisationSpecificStep.answerTemplates;

    if (answerTemplates) {
      let names = answerTemplates.map((x) => x.answer.text);
      let uniqueNames = names.filter((v, i, a) => a.indexOf(v) === i);
      if (names.length != uniqueNames.length) return false;
    }
    return true;
  }

  sortedAnswerTemplates(answerTemplates: IAnswerTemplate[]) {
    return answerTemplates.sort((a, b) => a.displayOrder - b.displayOrder);
  }
}
